/* eslint-disable @typescript-eslint/no-unused-vars */
import { FormControl, IconButton, MenuItem, Switch } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAppContext } from "AppContext";
import {
  CommonPagination,
  ConfirmDeleteModal,
  GKSelect,
  GKTable,
} from "components";
import { DeleteIcon } from "components/Icons";
import dayjs from "dayjs";
import DashboardLayout from "layouts/DashboardLayout";
import { useState } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import accessBoardService from "services/accessBoard";
import axiosInstance from "utils/axiosInstance";
import { ACCOUNTS_STATUS } from "utils/constants";
import { extraColumnsField } from "utils/helpers";
import { QueryKeys } from "utils/queryKey";

function AdminAccessBoard() {
  const {
    state: { currentUser, commonFilter },
  } = useAppContext();

  const { id } = useParams();

  const queryClient = useQueryClient();

  const [confirmationModal, setConfirmationModal] = useState(false);

  const [selectedClientId, setSelectedClientId] = useState(null);

  const requestBody = {
    requireTotalCount: true,
    take: commonFilter?.users?.take,
    skip: commonFilter?.users?.skip,
    sort: JSON.stringify(
      commonFilter?.users?.sortModel?.map((data: any) => {
        return {
          selector: data.field,
          desc: data.sort !== "asc",
        };
      })
    ),
    search: commonFilter?.users?.search || "",
  };

  const {
    data: accessList,
    isFetching,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [QueryKeys.ACCESS_LIST, requestBody],
    queryFn: () =>
      axiosInstance.get(`/user/all_user/`, { params: requestBody }),
  });

  const { mutate: handleUpdateAccountType } = useMutation({
    mutationKey: ["UPDATE_ACCOUNT_TYPE"],
    mutationFn: (data: any) =>
      axiosInstance.post(`/user/${data.phone}/account_type/`, {
        account_type: data.account_type,
      }),
    onSuccess: (response) => {
      refetch();
      toast.success(
        response?.data?.message || "Account type updated successfully"
      );
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Something went wrong");
    },
  });

  const { mutate: deleteClient } = useMutation({
    mutationKey: ["DELETE_CLIENT"],
    mutationFn: (clientId) =>
      axiosInstance.delete(`/super_admin/${clientId}/delete-user/`),
    onSuccess: (response) => {
      refetch();
      toast.success(response?.data?.message || "Update Broker Successfully");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Something went wrong");
    },
  });

  const cusPagination = () => {
    return (
      <CommonPagination
        name="users"
        totalCount={accessList?.data.totalCount || 0}
      />
    );
  };

  const columns: GridColDef[] = [
    {
      ...extraColumnsField("User Name", "name", 0, 1),
      valueGetter: (value) => value.value || "-",
    },

    {
      ...extraColumnsField("User Phone", "phone", 0, 1),
      valueGetter: (value) => value.value || "-",
    },

    {
      ...extraColumnsField("User Email", "email", 220, 1),
      valueGetter: (value) => value.value || "-",
    },
    {
      ...extraColumnsField("Is Active", "isActive", 100, 1),
      renderCell: (item) => (item?.row?.isActive ? "Yes" : "No"),
    },
    {
      ...extraColumnsField("Is Verified", "isVerified", 100, 1),
      renderCell: (item) => (item?.row?.isVerified ? "Yes" : "No"),
    },
    {
      ...extraColumnsField("User Role", "userRole", 100, 1),
    },
    {
      ...extraColumnsField("Created On", "dateJoined", 0, 1),
      valueFormatter: (data) => dayjs(data?.value).format("DD-MM-YYYY"),
    },
    {
      ...extraColumnsField("Grant Access", "grantAccess", 100, 1),
      align: "right",
      headerAlign: "right",
      renderCell: (item) => (
        <Switch
          checked={item?.row?.grantAccess}
          disabled={item?.row?.grantAccess === true}
          onChange={async (value) => {
            const response = await accessBoardService.grantAccess({
              userPhoneNumber: item?.row?.phone,
              hasAccess: value.target.checked,
            });
            if (response.status !== 200) {
              toast.error("Failed to grant access");
            }
            toast.success(
              `Access ${value ? "granted" : "revoked"} successfully`
            );
            queryClient.invalidateQueries([QueryKeys.ACCESS_LIST]);
          }}
        />
      ),
    },

    currentUser.phone === "7016333333" && {
      ...extraColumnsField("Account Type", "accountType", 140, 1),
      renderCell: (item) => {
        const selectedValue = item?.row?.accountType || "";

        return (
          <FormControl variant="standard" fullWidth>
            <GKSelect
              size="small"
              inputLabel=""
              name="accountType"
              value={selectedValue}
              onChange={(e) => {
                const selectedAccountType = e.target.value;
                handleUpdateAccountType({
                  phone: item?.row?.phone,
                  account_type: selectedAccountType,
                });
              }}
            >
              {ACCOUNTS_STATUS.map((dataItem, i) => (
                <MenuItem key={i} value={dataItem.value}>
                  {dataItem.name}
                </MenuItem>
              ))}
            </GKSelect>
          </FormControl>
        );
      },
    },

    currentUser.phone === "7016333333" && {
      ...extraColumnsField("Action", "action", 60, 1),
      align: "right",
      headerAlign: "right",
      renderCell: (item) => (
        <IconButton
          color="error"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedClientId(item?.row?.id);
            setConfirmationModal(true);
          }}
        >
          <DeleteIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <DashboardLayout title="Client Board">
      <GKTable
        name="users"
        columns={columns}
        rows={accessList?.data?.data || []}
        loading={isFetching}
        CustomPagination={!isLoading && cusPagination}
      />
      {confirmationModal && (
        <ConfirmDeleteModal
          open={confirmationModal}
          setOpen={setConfirmationModal}
          onDelete={() => deleteClient(selectedClientId)}
          title="Delete User"
          isDelete
        />
      )}
    </DashboardLayout>
  );
}

export default AdminAccessBoard;
